import LockIcon from '@mui/icons-material/LockSharp';
import {
	Button,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	TextField,
} from '@mui/material';
import { Container } from '@mui/system';
import { useMutation } from '@parkchirp/api-client';
import { FC, useCallback, useEffect, useState } from 'react';

import { UPDATE_PARKER_SEQUENCE_NUMBER } from '../../../mutations';
import { SetShowSnackbar } from '../../../types';
import { SelectedParker, SequencingTierParker } from '../../../types/leases';

type SequencingParkerListItemProps = {
	disabled: boolean;
	parker: SequencingTierParker;
	inBulkSequencingMode: boolean;
	maxSequenceNumber: number;
	setShowSnackbar: SetShowSnackbar;
	addSelectedParker: ({ id, sequenceNumber }: SelectedParker) => void;
	removeSelectedParker: (parkerId: string) => void;
	handleWaitForResequencing: () => Promise<void>;
};

export const SequencingUnfilledSlot: FC = () => (
	<ListItem sx={{ width: '100%' }} alignItems='flex-start'>
		<ListItemText primary='- Available -' />
	</ListItem>
);

export const SequencingParkerListItem: FC<SequencingParkerListItemProps> = ({
	disabled,
	parker,
	inBulkSequencingMode,
	maxSequenceNumber,
	addSelectedParker,
	removeSelectedParker,
	setShowSnackbar,
	handleWaitForResequencing,
}) => {
	const [editMode, setEditMode] = useState(false);
	const [selected, setSelected] = useState<boolean>(false);
	const [sequenceNumber, setSequenceNumber] = useState<number>(parker.sequenceNumber);
	const [sequenceNumberLocked, setSequenceNumberLocked] = useState<boolean>(parker.sequenceNumberLocked);
	const [updateParkerSequenceNumber, { loading: sequenceNumberUpdating }] =
		useMutation(UPDATE_PARKER_SEQUENCE_NUMBER);

	useEffect(() => {
		if (!inBulkSequencingMode) {
			setSelected(false);
		}
	}, [inBulkSequencingMode, setSelected]);

	const validateSequenceNumber = useCallback(() => {
		if (!Number.isInteger(sequenceNumber)) {
			return 'Sequence Number must be a whole number';
		}

		if (sequenceNumber > maxSequenceNumber) {
			return `Invalid Sequence Number - Sequence Number must be less than ${maxSequenceNumber}`;
		}

		return null;
	}, [sequenceNumber, maxSequenceNumber]);

	const handleSelected = useCallback(
		(checkboxValue: boolean) => {
			setSelected(checkboxValue);

			const { id: parkerId, sequenceNumber: parkerSequenceNumber } = parker;

			if (checkboxValue === true) {
				addSelectedParker({
					id: parkerId,
					sequenceNumber: parkerSequenceNumber,
				});
			} else {
				removeSelectedParker(parker.id);
			}
		},
		[parker, setSelected, addSelectedParker, removeSelectedParker],
	);

	const handleSave = useCallback(async () => {
		const validationError = validateSequenceNumber();

		if (validationError) {
			setShowSnackbar({
				message: validationError,
				severity: 'error',
				autoHideDuration: null,
			});

			return;
		}

		try {
			await updateParkerSequenceNumber({
				variables: {
					parkerId: parker.id,
					sequenceNumber,
					sequenceNumberLocked,
				},
				context: { apiVersion: 'v2' },
				onCompleted: async () => {
					setShowSnackbar({
						message: 'Sequence number updated',
						severity: 'success',
						autoHideDuration: 3000,
					});

					setEditMode(false);
					await handleWaitForResequencing();
				},
			});
		} catch {
			setShowSnackbar({
				message: 'Failed to update sequence number',
				severity: 'error',
				autoHideDuration: 5000,
			});
		}
	}, [
		parker,
		sequenceNumber,
		sequenceNumberLocked,
		validateSequenceNumber,
		setShowSnackbar,
		updateParkerSequenceNumber,
		handleWaitForResequencing,
	]);

	return (
		<ListItem
			sx={{ width: '100%' }}
			alignItems='flex-start'
			secondaryAction={
				<Container>
					{!editMode && (
						<>
							{sequenceNumberLocked && (
								<LockIcon fontSize='small' sx={{ verticalAlign: 'middle', marginRight: '10px' }} />
							)}
							<span>{sequenceNumber}</span>
							<Button
								variant='contained'
								size='small'
								disabled={disabled}
								sx={{ marginLeft: '15px' }}
								onClick={() => setEditMode(true)}
							>
								Edit
							</Button>
						</>
					)}
					{editMode && (
						<>
							<TextField
								sx={{ maxWidth: '150px' }}
								size='small'
								label='Sequence Number'
								type='number'
								value={sequenceNumber}
								onChange={(e) => setSequenceNumber(Number(e.target.value))}
								onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
							/>
							<FormControlLabel
								sx={{ marginLeft: '15px' }}
								control={
									<Checkbox
										checked={sequenceNumberLocked}
										size='small'
										onChange={(e) => setSequenceNumberLocked(e.target.checked)}
									/>
								}
								label='Locked'
							/>
							<Button
								variant='outlined'
								size='small'
								sx={{ marginLeft: '15px' }}
								onClick={() => {
									setSequenceNumber(parker.sequenceNumber);
									setSequenceNumberLocked(parker.sequenceNumberLocked);
									setEditMode(false);
								}}
							>
								Cancel
							</Button>
							<Button
								variant='contained'
								size='small'
								disabled={disabled}
								sx={{ marginLeft: '15px' }}
								onClick={handleSave}
							>
								{sequenceNumberUpdating ? (
									<CircularProgress style={{ height: '20px', width: '20px' }} />
								) : (
									<span>Save</span>
								)}
							</Button>
						</>
					)}
				</Container>
			}
		>
			{inBulkSequencingMode && (
				<ListItemButton dense disableGutters>
					<ListItemIcon>
						<Checkbox edge='start' checked={selected} onChange={(e) => handleSelected(e.target.checked)} />
					</ListItemIcon>
					<ListItemText
						primary={`${parker.firstName} ${parker.lastName}`}
						secondary={`AC-${parker.accountId}`}
					/>
				</ListItemButton>
			)}
			{!inBulkSequencingMode && (
				<ListItemText primary={`${parker.firstName} ${parker.lastName}`} secondary={`AC-${parker.accountId}`} />
			)}
		</ListItem>
	);
};
